/**
 * Adaptive Algorithm (Pace-Driven) — DEFAULT
 *
 * Pace-driven predictions anchored to live session data. Adapts to current
 * weekend performance with AI-tuned driver corrections.
 *
 * Core approach: Hybrid Pace Score + MC Correction
 *   finalPoints = paceScore + (1 - paceWeight) × clamp(mcCorrection, -8, +8)
 *
 * Where:
 * - paceScore = lookup from session position → expected fantasy points
 * - mcCorrection = MC expectedPoints - paceScore (driver-specific adjustment)
 * - paceWeight = stage-dependent trust in pace score vs MC
 *
 * At pre_weekend (no session data): falls back to MC + recent form blend.
 * As sessions progress: paceWeight increases, anchoring predictions to live pace.
 */

import { ADAPTIVE_CONFIG } from "../../prediction";
import {
  estimateConstructorParams,
  estimateDriverParams,
} from "../../prediction/component-model";
import { runDriverMonteCarlo } from "../../prediction/monte-carlo";
import {
  applySeasonDiscount,
  computeWeights,
  weightedMean,
} from "../../prediction/recency-weights";
import { adjustParamsForStage } from "../../prediction/stage-adjustments";
import { getTrackClassification } from "../../statistics/track-classification";
import { calculateSimplePriceChange } from "../price-change";
import type {
  PredictionAlgorithm,
  PredictionResult,
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
  TeamCalculatorRaceContext,
} from "../types";

// ============================================================================
// Pace Score — Piecewise Linear Model from Historical Data
// ============================================================================

/**
 * Piecewise linear model mapping session position → expected fantasy points.
 * Derived from 1,460 driver_fantasy_result records (2023-2026 seasons),
 * including DNF expected value (DNFs included in averages, 54 samples/position).
 *
 * Two segments capture distinct grid regimes:
 *
 * Front runners (P1-P10): Steep drop — each grid position costs ~3.2 pts.
 *   Regular: y = 35.0 - 3.20 × pos  (R² = 0.933, n=540)
 *   Sprint:  y = 39.6 - 3.34 × pos  (R² = 0.931, n=180)
 *
 * Backfield (P11-P20): Gentle negative slope from the P10 boundary.
 *   Raw per-position averages are noisy (recovery drives inflate P15-P19),
 *   so we use a calibrated slope that transitions from the P10 boundary
 *   (~3 pts regular, ~6 pts sprint) down to approximately -3 pts at P20.
 *   This matches competitor predictions for tail-end drivers (PER, BOT ≈ -3).
 *   Slope: -0.65/pos — drops ~6.5 pts over 10 positions.
 *
 * Continuous function handles fractional positions (constructor avg P4.5).
 */
// Front intercepts raised +3 from pure regression to match raw P1-P3 averages
// (regression underestimates P1-P3 because P8-P10 pull the line down).
// Regular raw P1=36.3 → fit was 31.8, now 34.8. Sprint raw P1=39.2 → was 36.3, now 39.3.
const REG_FRONT_INTERCEPT = 38.0;
const REG_FRONT_SLOPE = -3.2;
const SPR_FRONT_INTERCEPT = 42.6;
const SPR_FRONT_SLOPE = -3.34;

// Backfield: decoupled from front model to prevent tail-end inflation.
// Anchored at historical P10 averages (regular ≈ 6, sprint ≈ 7.5), then
// drops 0.65 pts per position through P20 (PER/BOT at P19-P21 → negative).
const BACK_SLOPE = -0.65;
const REG_BACK_ANCHOR = 6.0; // raw P10 avg = 6.4
const SPR_BACK_ANCHOR = 7.5; // raw P10 avg = 7.5

function lookupPaceScore(position: number, isSprintWeekend: boolean): number {
  const pos = Math.max(1, Math.min(20, position));
  if (isSprintWeekend) {
    return pos <= 10
      ? SPR_FRONT_INTERCEPT + SPR_FRONT_SLOPE * pos
      : SPR_BACK_ANCHOR + BACK_SLOPE * (pos - 10);
  }
  return pos <= 10
    ? REG_FRONT_INTERCEPT + REG_FRONT_SLOPE * pos
    : REG_BACK_ANCHOR + BACK_SLOPE * (pos - 10);
}

// ============================================================================
// Pace Weight Schedule (stage-dependent)
// ============================================================================

/** Regular weekend pace weight schedule */
const REGULAR_PACE_WEIGHT: Record<string, number> = {
  pre_weekend: 0.0,
  after_fp1: 0.3,
  after_fp2: 0.45,
  after_fp3: 0.6, // LOCK stage
  after_quali: 0.85,
  after_race: 0.0,
};

/** Sprint weekend pace weight schedule */
const SPRINT_PACE_WEIGHT: Record<string, number> = {
  pre_weekend: 0.0,
  after_fp1: 0.3,
  after_sprint_quali: 0.6, // LOCK stage
  after_sprint: 0.75,
  after_quali: 0.85,
  after_race: 0.0,
};

function getPaceWeight(stage: string, isSprintWeekend: boolean): number {
  if (isSprintWeekend) {
    return SPRINT_PACE_WEIGHT[stage] ?? 0.0;
  }
  return REGULAR_PACE_WEIGHT[stage] ?? 0.0;
}

// ============================================================================
// Session Position Selection
// ============================================================================

/**
 * Pick the most relevant session position per stage.
 * Returns null if no session data available (pre_weekend).
 */
function getSessionPosition(
  driver: TeamCalculatorDriver,
  stage: string,
  isSprintWeekend: boolean,
): number | null {
  if (isSprintWeekend) {
    switch (stage) {
      case "after_fp1":
        return driver.fp1Position ?? null;
      case "after_sprint_quali":
        return driver.sprintQualiPosition ?? driver.fp1Position ?? null;
      case "after_sprint":
        return driver.sprintPosition ?? driver.sprintQualiPosition ?? null;
      case "after_quali":
        return driver.qualiPosition ?? driver.sprintPosition ?? null;
      default:
        return null;
    }
  }
  // Regular weekend
  switch (stage) {
    case "after_fp1":
      return driver.fp1Position ?? null;
    case "after_fp2":
      return driver.fp2Position ?? driver.fp1Position ?? null;
    case "after_fp3":
      return driver.fp3Position ?? driver.fp2Position ?? null;
    case "after_quali":
      return driver.qualiPosition ?? driver.fp3Position ?? null;
    default:
      return null;
  }
}

/**
 * Get constructor session position from averaged driver positions.
 */
function getConstructorSessionPosition(
  ctor: TeamCalculatorConstructor,
  stage: string,
  isSprintWeekend: boolean,
): number | null {
  if (isSprintWeekend) {
    switch (stage) {
      case "after_fp1":
        return ctor.fp1AvgPosition ?? null;
      case "after_sprint_quali":
        return ctor.sprintQualiAvgPosition ?? ctor.fp1AvgPosition ?? null;
      case "after_sprint":
        return ctor.sprintAvgPosition ?? ctor.sprintQualiAvgPosition ?? null;
      case "after_quali":
        return ctor.qualiAvgPosition ?? ctor.sprintAvgPosition ?? null;
      default:
        return null;
    }
  }
  switch (stage) {
    case "after_fp1":
      return ctor.fp1AvgPosition ?? null;
    case "after_fp2":
      return ctor.fp2AvgPosition ?? ctor.fp1AvgPosition ?? null;
    case "after_fp3":
      return ctor.fp3AvgPosition ?? ctor.fp2AvgPosition ?? null;
    case "after_quali":
      return ctor.qualiAvgPosition ?? ctor.fp3AvgPosition ?? null;
    default:
      return null;
  }
}

// ============================================================================
// Pre-Weekend Fallback (MC + Recent Form blend)
// ============================================================================

/**
 * When paceWeight = 0 (no session data), fall back to MC + recent form blend.
 * Same as Momentum at pre_weekend (50/50).
 * Includes Fix A: DNF in recent form uses MC expected points as proxy.
 */
function preWeekendFallback(
  mcExpectedPoints: number,
  driver: TeamCalculatorDriver,
  raceContext: TeamCalculatorRaceContext,
  components: any,
): number {
  const history = driver.raceHistory;
  if (history.length === 0) return mcExpectedPoints;

  const r1 = history[history.length - 1];
  const r2 = history.length >= 2 ? history[history.length - 2] : null;

  let recentScore: number;
  if (r2 && r2.season >= raceContext.season) {
    recentScore = 0.6 * r1.totalPoints + 0.4 * r2.totalPoints;
  } else {
    recentScore = r1.totalPoints;
  }

  // Fix A: When R-1 was a DNF, use MC expected points as proxy
  if (r1.raceDnf) {
    recentScore = mcExpectedPoints;
  }

  // Discount pit-lane-start inflation
  const r1IsPitLaneStart = r1.qualifyingPosition == null;
  if (r1IsPitLaneStart && !r1.raceDnf) {
    const r1Contribution = r2 && r2.season >= raceContext.season ? 0.6 : 1.0;
    const discount = 0.35;
    recentScore -= r1Contribution * r1.totalPoints * (1 - discount);
  }

  // Sprint bonus adjustment
  if (
    raceContext.isSprintWeekend &&
    !r1.isSprintWeekend &&
    !r1IsPitLaneStart &&
    !r1.raceDnf &&
    components
  ) {
    const sprintBonus =
      components.sprintPositionEV +
      components.sprintOvertakesEV +
      components.sprintFastestLapEV;
    recentScore += sprintBonus;
  }

  // 50/50 blend at pre_weekend
  return 0.5 * recentScore + 0.5 * mcExpectedPoints;
}

// ============================================================================
// Race Context Builder
// ============================================================================

function buildRaceContext(raceContext: TeamCalculatorRaceContext) {
  return {
    raceSlug: raceContext.raceName ?? "",
    trackType: getTrackClassification(raceContext.raceName ?? ""),
    isSprintWeekend: raceContext.isSprintWeekend,
    raceRound: raceContext.raceRound,
    season: raceContext.season,
  };
}

// ============================================================================
// MC Correction Clamp
// ============================================================================

const MC_CORRECTION_CAP = 12;

// ============================================================================
// Algorithm Export
// ============================================================================

export const adaptiveAlgorithm: PredictionAlgorithm = {
  metadata: {
    id: "adaptive",
    name: "Adaptive",
    description:
      "Pace-driven predictions anchored to live session data. Adapts to current weekend performance with AI-tuned driver corrections.",
    icon: "Activity",
    isAdvanced: true,
  },

  predictDriver(
    driver: TeamCalculatorDriver,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const ctx = buildRaceContext(raceContext);
    const baseParams = estimateDriverParams(driver, ctx, ADAPTIVE_CONFIG);
    const params = adjustParamsForStage(
      baseParams,
      driver,
      raceContext.currentStage,
    );
    const mcResult = runDriverMonteCarlo(
      params,
      raceContext.isSprintWeekend,
      10_000,
      raceContext.season,
    );

    const paceWeight = getPaceWeight(
      raceContext.currentStage,
      raceContext.isSprintWeekend,
    );

    let finalPoints: number;

    if (paceWeight === 0) {
      // Pre-weekend: no session data → MC + recent form fallback
      finalPoints = preWeekendFallback(
        mcResult.expectedPoints,
        driver,
        raceContext,
        mcResult.components,
      );
    } else {
      // Session data available: pace-driven hybrid
      const sessionPos = getSessionPosition(
        driver,
        raceContext.currentStage,
        raceContext.isSprintWeekend,
      );
      // Null positions → P20 (same convention as existing code)
      const effectivePos = sessionPos ?? 20;
      const paceScore = lookupPaceScore(
        effectivePos,
        raceContext.isSprintWeekend,
      );

      // MC correction: driver-specific adjustment capped at ±8
      const rawCorrection = mcResult.expectedPoints - paceScore;
      const mcCorrection = Math.max(
        -MC_CORRECTION_CAP,
        Math.min(MC_CORRECTION_CAP, rawCorrection),
      );

      // finalPoints = paceScore + (1 - paceWeight) × clamp(mcCorrection, -8, +8)
      finalPoints = paceScore + (1 - paceWeight) * mcCorrection;
    }

    return {
      entityId: driver.driverSlug,
      predictedPoints: Math.round(finalPoints * 10) / 10,
      confidence: mcResult.confidence,
      p10: mcResult.p10,
      p90: mcResult.p90,
      stdDev: mcResult.stdDev,
      dnfProb: mcResult.dnfProb,
      components: mcResult.components,
    };
  },

  predictConstructor(
    ctor: TeamCalculatorConstructor,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const history = ctor.raceHistory;

    if (history.length === 0) {
      return {
        entityId: ctor.slug,
        predictedPoints: 20,
        confidence: 0.1,
      };
    }

    // Compute historical base (same as momentum — exponential decay + season discount)
    const reversed = [...history].reverse();
    const rawWeights = computeWeights(reversed.length, ADAPTIVE_CONFIG);
    const weights = applySeasonDiscount(
      rawWeights,
      reversed,
      raceContext.season,
    );
    const points = reversed.map((h) => h.totalPoints);
    let historicalBase = weightedMean(points, weights);

    // Early-season prior regression (same as momentum)
    const currentSeasonRaces = reversed.filter(
      (h) => h.season >= raceContext.season,
    );
    const currentSeasonSprintRaces = reversed.filter(
      (h) => h.isSprintWeekend && h.season >= raceContext.season,
    );
    let prevAvg = 0;
    if (currentSeasonRaces.length < 3) {
      const prevSeasonPoints = reversed
        .filter((h) => h.season < raceContext.season)
        .map((h) => h.totalPoints);
      if (prevSeasonPoints.length > 0) {
        prevAvg =
          prevSeasonPoints.reduce((a, b) => a + b, 0) / prevSeasonPoints.length;
        const getConfidence = (prior: number, races: number) => {
          const tier =
            prior >= 40 ? "strong" : prior >= 25 ? "mid" : "volatile";
          if (races === 1)
            return tier === "strong" ? 0.5 : tier === "mid" ? 0.35 : 0.15;
          return tier === "strong" ? 0.65 : tier === "mid" ? 0.5 : 0.35;
        };
        const currentConfidence = getConfidence(
          prevAvg,
          currentSeasonRaces.length,
        );
        historicalBase =
          currentConfidence * historicalBase +
          (1 - currentConfidence) * prevAvg;
      }
    }

    // Sprint adjustment on historical base
    if (raceContext.isSprintWeekend) {
      if (currentSeasonSprintRaces.length >= 2) {
        const sprintPts = currentSeasonSprintRaces.map((h) => h.totalPoints);
        const sprintAvg =
          sprintPts.reduce((a, b) => a + b, 0) / sprintPts.length;
        historicalBase = sprintAvg * 0.7 + historicalBase * 0.3;
      } else {
        const isLowConfidenceTeam = prevAvg > 0 && prevAvg < 25;
        const SPRINT_UPLIFT =
          currentSeasonSprintRaces.length === 0 && isLowConfidenceTeam
            ? 0.1
            : 0.2;
        historicalBase *= 1 + SPRINT_UPLIFT;
      }
    }

    const paceWeight = getPaceWeight(
      raceContext.currentStage,
      raceContext.isSprintWeekend,
    );

    let avgPoints: number;

    if (paceWeight === 0) {
      // Pre-weekend: use historical base (same as momentum constructor logic)
      avgPoints = historicalBase;
    } else {
      // Pace-driven: use constructor's average session position
      const sessionPos = getConstructorSessionPosition(
        ctor,
        raceContext.currentStage,
        raceContext.isSprintWeekend,
      );

      if (sessionPos == null) {
        avgPoints = historicalBase;
      } else {
        // Constructor pace score = driverPaceScore(avgSessionPos) × 2 + historicalPitStopEV
        const ctorParams = estimateConstructorParams(ctor, ADAPTIVE_CONFIG);
        const driverPace = lookupPaceScore(
          sessionPos,
          raceContext.isSprintWeekend,
        );
        const paceScore = driverPace * 2 + ctorParams.expectedPitStopPoints;

        // MC correction: historical base acts as the "MC" for constructors
        const rawCorrection = historicalBase - paceScore;
        const mcCorrection = Math.max(
          -MC_CORRECTION_CAP,
          Math.min(MC_CORRECTION_CAP, rawCorrection),
        );

        avgPoints = paceScore + (1 - paceWeight) * mcCorrection;
      }
    }

    const ctorParams = estimateConstructorParams(ctor, ADAPTIVE_CONFIG);

    return {
      entityId: ctor.slug,
      predictedPoints: Math.round(avgPoints * 10) / 10,
      confidence: ctorParams.confidence,
    };
  },

  calculatePriceChange(
    predictedPoints: number,
    currentPrice: number,
    tier: "A" | "B",
    priceFloor: number,
  ): number {
    return calculateSimplePriceChange(
      predictedPoints,
      tier,
      currentPrice,
      priceFloor,
    );
  },
};
